// Pages/ResetPassword.jsx
//
// Standalone page the "Reset your password" email button links to. Reads the
// email + one-time code from the query string (?email=&code=), so the user
// only has to choose a new password. Also works if opened without params — the
// fields are editable.
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { authService } from "../services/auth.service";

const INPUT =
  "w-full rounded-2xl bg-[#F3F4F6] border border-slate-200 px-4 py-3.5 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition";

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState((params.get("code") || "").replace(/\D/g, ""));
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) return setError("Enter the email for your account.");
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your email.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");

    setLoading(true);
    try {
      await authService.resetPassword({ email, code, newPassword: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(err?.message || "This reset code is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center px-5 py-10">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-6 md:p-8 max-w-md w-full">
        {done ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-[#E8F5E1] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={30} className="text-[#0A4730]" />
            </div>
            <h2 className="text-xl font-semibold text-[#0A2540] mb-2">Password reset</h2>
            <p className="text-sm text-[#0A2540]/50">Taking you to the login page…</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#E8F5E1] flex items-center justify-center mx-auto mb-5">
                <ShieldCheck size={28} className="text-[#0A4730]" />
              </div>
              <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">Reset password</h1>
              <p className="text-sm text-[#0A2540]/50 mt-2">
                Enter the code from your email and choose a new password.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <input
                type="email"
                placeholder="Email"
                className={INPUT}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                className={`${INPUT} tracking-[0.4em] text-center font-semibold`}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
              />
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  className={INPUT}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                className={INPUT}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary font-bold py-4 rounded-2xl w-full flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Resetting…" : "Reset password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
